import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Field } from './Field.js';
import { Input } from './Input.js';
import { Button } from './Button.js';

// ── Elegir un color de marca ─────────────────────────────────────────────────
// Promovido desde FormDetailPage (2026-08-31, REGLA #13): vivía ahí como
// componente local con tres usos, y el cuarto —el acento de los correos de
// facturación, en Settings → General— lo volvió compartido entre dos módulos.
// Copiarlo habría sido el "variante local" que la regla prohíbe.
//
// El valor es `string | null`: null significa "sin elegir, se usa el que
// corresponda por defecto", que es un estado distinto de "eligió justo el color
// que coincide con el default". Por eso el botón de restablecer aparece solo
// cuando hay un valor propio: es lo único que distingue los dos casos en
// pantalla.
//
// Se confirma al salir del campo (onBlur) y no en cada cambio: un selector de
// color dispara onChange en cada píxel que se arrastra, y eso serían decenas de
// escrituras por elección.

export interface ColorFieldProps {
  label: string;
  hint: string;
  /** null = sin color propio; se muestra `fallback`. */
  value: string | null;
  /** El color por defecto de este contexto, en hex. */
  fallback: string;
  onCommit: (hex: string) => void;
  onReset: () => void;
  /** Lo que va debajo del control — típicamente el aviso de contraste. */
  children?: ReactNode;
}

export function ColorField({ label, hint, value, fallback, onCommit, onReset, children }: ColorFieldProps) {
  const { t } = useTranslation();
  const [local, setLocal] = useState(value ?? fallback);
  useEffect(() => { setLocal(value ?? fallback); }, [value, fallback]);

  return (
    <Field label={label} hint={hint}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
          <Input
            type="color"
            size="sm"
            value={local}
            onChange={setLocal}
            onBlur={() => { if (local !== (value ?? fallback)) onCommit(local); }}
          />
          <code style={{ fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font-mono)', color: 'var(--evo-fg-3)' }}>{local}</code>
          {value !== null && (
            <Button variant="ghost" tone="muted" size="sm" type="button" onClick={onReset}>
              {t('forms.appearance.reset')}
            </Button>
          )}
        </div>
        {children}
      </div>
    </Field>
  );
}
